/**
 * Composes multiple callbacks into a single function that, when invoked,
 * calls all provided callbacks in order.
 *
 * This is useful for combining multiple event handlers or cleanup functions
 * into a single handler. Safely handles `undefined` or `void` values in the callback list.
 *
 * @param callbacks - A list of callback functions (or `undefined`/`void` values) to compose.
 * @returns A function that, when called, invokes all provided callbacks sequentially.
 *
 * @example
 * ```tsx
 * // Combining multiple onClick handlers
 * <button onClick={composeCallbacks(handleAnalytics, handleNavigation, onClickProp)} />
 *
 * // Combining cleanup functions in useEffect
 * useEffect(() => {
 *   const cleanup1 = subscribeToEvent();
 *   const cleanup2 = startTimer();
 *   return composeCallbacks(cleanup1, cleanup2);
 * }, []);
 *
 * // Safely handles undefined callbacks
 * const optionalHandler = condition ? handleClick : undefined;
 * <button onClick={composeCallbacks(requiredHandler, optionalHandler)} />
 * ```
 */
export const composeCallbacks = (...callbacks: ((() => void) | (() => unknown) | void)[]) => {
  return () => callbacks.forEach((callback) => callback?.());
};

// biome-ignore lint: allow empty block statements
export const noop = () => {};

type Result<T> = [null, T] | [Error, null];

type AttemptReturnType<F extends () => unknown> = [ReturnType<F>] extends [never]
  ? Result<never>
  : ReturnType<F> extends Promise<infer T>
    ? Promise<Result<T>>
    : Result<ReturnType<F>>;

/**
 * Attempts to execute a function and returns a tuple with the error or result.
 *
 * This function provides a safe way to execute synchronous or asynchronous functions
 * without throwing exceptions. Instead, errors are returned as part of the result tuple.
 *
 * @template T - The type of the value returned by the function
 * @param fn - A function to execute. Can be synchronous or return a Promise.
 * @returns For synchronous functions: A tuple `[null, value]` on success or `[Error, null]` on error.
 *          For async functions: A Promise that resolves to a tuple `[null, value]` on success or `[Error, null]` on error.
 *          TypeScript will narrow the type: when `error` is `null`, `value` is guaranteed to be of type `T` (not `T | null`).
 *
 * @example
 * ```ts
 * // Synchronous success - TypeScript knows value is number (not number | null)
 * const [error, value] = attempt(() => 42);
 * if (error === null) {
 *   // value is inferred as number here, not number | null
 *   console.log(value.toFixed(2)); // TypeScript allows this
 * }
 *
 * // Synchronous error
 * const [error, value] = attempt(() => {
 *   throw new Error('Something went wrong');
 * });
 * // error = Error('Something went wrong'), value = null
 *
 * // Async success - TypeScript knows value is string (not string | null)
 * const [error, value] = await attempt(async () => {
 *   return await fetchData();
 * });
 * if (!error) {
 *   // value is inferred as string here
 *   console.log(value.toUpperCase()); // TypeScript allows this
 * }
 *
 * // Async error
 * const [error, value] = await attempt(async () => {
 *   throw new Error('Network error');
 * });
 * // error = Error('Network error'), value = null
 * ```
 */
export function attempt<F extends () => unknown>(fn: F): AttemptReturnType<F> {
  try {
    const result = fn();

    if (result instanceof Promise) {
      return result.then((value) => [null, value]).catch((error: Error) => [error, null]) as AttemptReturnType<F>;
    }

    return [null, result] as AttemptReturnType<F>;
  } catch (error) {
    return [error instanceof Error ? error : new Error(String(error)), null] as AttemptReturnType<F>;
  }
}
